const user_request_error_message = "Unable to complete user request.";

export function ensure_trailing_slash(url) {
    if (!url) {
        return "";
    }

    return url.endsWith("/") ? url : `${url}/`;
}

export function extract_user_records(payload) {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (payload?.id) {
        return [payload];
    }

    return [];
}

export function normalize_user(user, fallback_id = "") {
    const role = user?.user_role?.role || null;
    const profile = user?.profile || null;

    return ({
        id: user?.id || fallback_id,
        username: user?.username || "",
        email: user?.email || "",
        first_name: user?.first_name || "",
        last_name: user?.last_name || "",
        phone_number: profile?.phone_number || "",
        role: role?.name || "",
        role_id: role?.id ?? null,
        is_active: user?.is_active !== undefined ? Boolean(user.is_active) : true,
        profile,
        user_role: user?.user_role || null,
        date_joined: user?.date_joined || "",
        raw: user
    });
}

export function extract_role_records(payload) {
    if (Array.isArray(payload)) {
        return payload;
    }

    return [];
}

export function normalize_role_option(role) {
    if (!role?.id || !role?.name) {
        return null;
    }

    return ({
        id: role.id,
        name: role.name,
        description: role.description || "",
        created_at: role.created_at || "",
        updated_at: role.updated_at || ""
    });
}

export function normalize_role_id(role_id) {
    if (role_id === undefined || role_id === null || role_id === "") {
        return null;
    }

    if (typeof role_id === "number") {
        return role_id;
    }

    const numeric = Number(role_id);
    return Number.isNaN(numeric) ? role_id : numeric;
}

function normalize_api_error_payload(error_payload) {
    return error_payload?.errors
        || error_payload?.detail
        || error_payload?.error
        || error_payload?.message
        || error_payload;
}

function format_api_error_message(messages) {
    return Array.isArray(messages) ? messages.join(", ") : messages;
}

function format_api_error_object(error_payload) {
    return Object.entries(error_payload)
        .map(([field, messages]) => `${field}: ${format_api_error_message(messages)}`)
        .join(" | ");
}

export function format_api_errors(error_payload) {
    const normalized_payload = normalize_api_error_payload(error_payload);

    if (!normalized_payload) {
        return user_request_error_message;
    }

    if (typeof normalized_payload === "string") {
        return normalized_payload;
    }

    if (Array.isArray(normalized_payload)) {
        return normalized_payload.join(", ");
    }

    if (typeof normalized_payload === "object") {
        return format_api_error_object(normalized_payload);
    }

    return user_request_error_message;
}

export const session_policy_default = "default";
export const session_policy_custom = "custom";
export const session_policy_unlimited = "unlimited";

export const session_policy_options = [
    { value: session_policy_default, label: "Default session" },
    { value: session_policy_custom, label: "Custom session" },
    { value: session_policy_unlimited, label: "Unlimited session" }
];

export function normalize_session_policy(policy) {
    const normalized_policy = String(policy || "").trim().toLowerCase();

    return session_policy_options.some((option) => option.value === normalized_policy)
        ? normalized_policy
        : session_policy_default;
}

export function normalize_session_timeout_minutes(timeout_minutes) {
    if (timeout_minutes === undefined || timeout_minutes === null || timeout_minutes === "") {
        return null;
    }

    const numeric = Number(timeout_minutes);
    return Number.isInteger(numeric) && numeric > 0 ? numeric : null;
}

export function get_session_policy_label(policy, timeout_minutes) {
    const normalized_policy = normalize_session_policy(policy);
    const normalized_minutes = normalize_session_timeout_minutes(timeout_minutes);

    if (normalized_policy === session_policy_unlimited) {
        return "Unlimited";
    }

    if (normalized_policy === session_policy_custom && normalized_minutes) {
        return `${normalized_minutes} min`;
    }

    return "Default";
}

export function validate_session_timeout_minutes(policy, timeout_minutes) {
    if (normalize_session_policy(policy) !== session_policy_custom) {
        return "";
    }

    if (timeout_minutes === undefined || timeout_minutes === null || String(timeout_minutes).trim() === "") {
        return "Custom session duration is required.";
    }

    return normalize_session_timeout_minutes(timeout_minutes)
        ? ""
        : "Custom session duration must be a whole number of at least 1 minute.";
}
